import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, guestId, fmtErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AudioPlayer, { INTERVALS } from "@/components/AudioPlayer";
import GuessDropdown from "@/components/GuessDropdown";
import GuessHistory from "@/components/GuessHistory";
import RevealModal from "@/components/RevealModal";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

export default function GameRoom({ mode }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [puzzle, setPuzzle] = useState(null);
  const [number, setNumber] = useState(0);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]); // {wrestler_id, text, correct, skipped}
  const [step, setStep] = useState(0); // 0..5
  const [finished, setFinished] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [showReveal, setShowReveal] = useState(false);
  const [score, setScore] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const audioUrl = puzzle ? `${API_BASE}/media/audio/${puzzle.id}` : null;
  const audioRef = useRef(null);
  const stopAudio = () => { try { audioRef.current?.stop(); } catch { /* noop */ } };

  // Stop audio on unmount / navigation.
  useEffect(() => () => stopAudio(), []);

  const loadPuzzle = useCallback(async () => {
    setLoading(true); setErrorMsg(""); setHistory([]); setStep(0); setFinished(false); setReveal(null);
    try {
      const { data } = await api.get(`/puzzles/daily/${mode}`);
      setPuzzle(data.puzzle);
      setNumber(data.number);
      if (data.puzzle) {
        const res = await api.get(`/play/result/${data.puzzle.id}`,
          { params: user ? {} : { guest_id: guestId() } });
        if (res.data.played) {
          setFinished(true);
          setScore(res.data.score);
          const fin = await api.post("/play/finalize", {
            puzzle_id: data.puzzle.id, won: res.data.won,
            interval_solved: res.data.interval_solved,
            attempts: res.data.attempts,
            is_daily: true, guest_id: guestId(),
          });
          setReveal(fin.data);
          setShowReveal(true);
          const rebuilt = [];
          const solved = res.data.interval_solved ?? 5;
          for (let i = 0; i < (res.data.won ? solved : 6); i++) {
            if (i < solved) rebuilt.push({ text: "—", correct: false, skipped: false });
            else if (res.data.won) rebuilt.push({ text: "—", correct: true, skipped: false });
          }
          setHistory(rebuilt);
        }
      }
    } catch (e) {
      setErrorMsg(fmtErr(e.response?.data?.detail) || "Failed to load puzzle");
    } finally {
      setLoading(false);
    }
  }, [mode, user]);

  useEffect(() => { loadPuzzle(); }, [loadPuzzle]);

  const finalize = async (won, intervalSolved, attempts) => {
    try {
      const { data } = await api.post("/play/finalize", {
        puzzle_id: puzzle.id, won, interval_solved: intervalSolved,
        attempts, guest_id: guestId(), is_daily: true,
      });
      setReveal(data);
      setScore(data.score);
      setShowReveal(true);
    } catch { toast.error("Failed to save result"); }
  };

  const onGuessSubmit = async (wrestler) => {
    stopAudio();
    try {
      const { data } = await api.post("/play/submit", {
        puzzle_id: puzzle.id, interval_index: step,
        wrestler_id: wrestler.id, skipped: false,
      });
      const entry = { wrestler_id: wrestler.id, text: wrestler.primary_name, correct: data.correct, skipped: false };
      const newHistory = [...history, entry];
      setHistory(newHistory);
      if (data.correct) {
        setFinished(true);
        await finalize(true, step, newHistory.length);
      } else if (step >= 5) {
        setFinished(true);
        await finalize(false, null, newHistory.length);
      } else {
        setStep(step + 1);
      }
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Guess failed");
    }
  };

  const skip = async () => {
    stopAudio();
    const entry = { text: "Skipped", correct: false, skipped: true };
    const newHistory = [...history, entry];
    setHistory(newHistory);
    if (step >= 5) {
      setFinished(true);
      await finalize(false, null, newHistory.filter((h) => !h.skipped).length);
    } else {
      setStep(step + 1);
    }
  };

  const share = () => {
    const squares = Array.from({ length: 6 }, (_, i) => {
      const h = history[i];
      if (!h) return "⬛";
      if (h.correct) return "🟩";
      if (h.skipped) return "🟨";
      return "🟥";
    }).join("");
    const label = mode === "theme" ? "Theme" : "Promo";
    const text = `POPLeSS #${number}\n\n${label}: ${squares}\nScore: ${score}\n\nPlay: ${window.location.origin}`;
    if (navigator.share) navigator.share({ text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success("Result copied to clipboard!"); }
  };

  if (loading) {
    return <div className="max-w-2xl mx-auto px-4 py-10 text-center text-slate-400" data-testid="game-loading">Loading arena…</div>;
  }
  if (errorMsg) {
    return <div className="max-w-2xl mx-auto px-4 py-10 text-center text-red-400" data-testid="game-error">{errorMsg}</div>;
  }
  if (!puzzle) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="no-puzzle">
        <div className="text-xs uppercase tracking-widest text-red-500 font-mono mb-2">Empty Arena</div>
        <h2 className="font-display text-3xl font-black mb-3">No puzzle scheduled today</h2>
        <p className="text-slate-400">The bell hasn't rung yet. Check back later.</p>
      </div>
    );
  }

  const maxInterval = INTERVALS[step];
  const excludeIds = history.filter((h) => h.wrestler_id).map((h) => h.wrestler_id);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-red-500">
            POPLeSS #{number || "—"} · Daily
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-white">
            {mode === "theme" ? "Entrance Theme" : "Promo Challenge"}
          </h1>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono uppercase text-slate-500">Round</div>
          <div className="font-display text-2xl font-black" data-testid="round-number">{step + 1}/6</div>
        </div>
      </div>

      <AudioPlayer
        ref={audioRef}
        src={audioUrl}
        startAt={puzzle.audio_start_seconds || 0}
        maxInterval={maxInterval}
      />

      <div className="flex flex-wrap justify-center gap-2 mt-4" data-testid="interval-pills">
        {INTERVALS.map((s, i) => {
          const done = i < history.length;
          const active = i === step && !finished;
          const guess = history[i];
          let cls = "bg-slate-800 text-slate-400 border-slate-700";
          if (guess?.correct) cls = "bg-emerald-600/30 text-emerald-200 border-emerald-500";
          else if (guess?.skipped) cls = "bg-amber-600/20 text-amber-300 border-amber-600/40";
          else if (done) cls = "bg-red-600/20 text-red-300 border-red-800/50";
          if (active) cls += " ring-2 ring-red-500";
          return (
            <span key={i} className={`text-xs font-mono px-2.5 py-1 rounded-full border ${cls}`}
                  data-testid={`interval-pill-${i}`}>
              {s < 1 ? s.toFixed(1) : s.toFixed(0)}s
            </span>
          );
        })}
      </div>

      {!finished && (
        <div className="mt-6">
          <GuessDropdown
            excludeIds={excludeIds}
            onSubmit={onGuessSubmit}
            onSkip={skip}
            disabled={finished}
            remaining={6 - step}
          />
        </div>
      )}

      <GuessHistory history={history} />

      {finished && !showReveal && (
        <div className="mt-6 text-center">
          <button className="text-sm font-mono uppercase tracking-widest text-red-500 hover:text-red-400"
            onClick={() => setShowReveal(true)} data-testid="show-reveal-again">
            Show Reveal
          </button>
        </div>
      )}

      <RevealModal
        open={showReveal} onClose={() => setShowReveal(false)}
        puzzleId={puzzle.id} mode={mode} number={number || 0}
        won={history.some((h) => h.correct)} score={score}
        history={history} revealData={reveal}
        onShare={share}
        onNext={() => nav(mode === "theme" ? "/daily/promo" : "/daily/theme")}
      />
    </div>
  );
}
